import React, { useState, useEffect } from 'react';
import { Plus, Trash2, CheckCircle, XCircle, RefreshCw, ExternalLink, Shield, Users } from 'lucide-react';
import { getGBPAccounts, initiateGBPAuth, removeGBPAccount, testGBPConnection } from '../services/googleBusinessApi';
import { usePermissions } from '../contexts/PermissionsContext';

const GoogleBusinessSettings = () => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [testingAccount, setTestingAccount] = useState(null);
  const [removingAccount, setRemovingAccount] = useState(null);
  const { hasPermission, isAdmin } = usePermissions();

  useEffect(() => {
    loadAccounts();
  }, []);

  const loadAccounts = async () => {
    try {
      setLoading(true);
      const data = await getGBPAccounts();
      setAccounts(data.accounts || []);
    } catch (error) {
      console.error('Failed to load GBP accounts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddAccount = async () => {
    if (!isAdmin) {
      alert('Only administrators can add Google Business Profile accounts.');
      return;
    }

    try {
      const authUrl = await initiateGBPAuth();
      // Open OAuth flow in new window
      const width = 600;
      const height = 700;
      const left = window.screen.width / 2 - width / 2;
      const top = window.screen.height / 2 - height / 2;

      const authWindow = window.open(
        authUrl,
        'Google Business Profile Auth',
        `width=${width},height=${height},left=${left},top=${top}`
      );

      // Listen for OAuth completion
      const checkWindowClosed = setInterval(() => {
        if (authWindow.closed) {
          clearInterval(checkWindowClosed);
          // Reload accounts after OAuth completion
          loadAccounts();
        }
      }, 1000);
    } catch (error) {
      console.error('Failed to initiate auth:', error);
      if (error.message.includes('403')) {
        alert('Only administrators can add accounts.');
      } else {
        alert('Failed to start authentication. Please try again.');
      }
    }
  };

  const handleTestConnection = async (gmailAccount) => {
    try {
      setTestingAccount(gmailAccount);
      const result = await testGBPConnection(gmailAccount);
      alert(`Connection test ${result.success ? 'successful' : 'failed'}: ${result.message}`);
    } catch (error) {
      console.error('Failed to test connection:', error);
      alert('Failed to test connection. Please try again.');
    } finally {
      setTestingAccount(null);
    }
  };

  const handleRemoveAccount = async (gmailAccount) => {
    if (!isAdmin) {
      alert('Only administrators can remove Google Business Profile accounts.');
      return;
    }

    // eslint-disable-next-line no-restricted-globals
    if (!confirm(`Are you sure you want to remove ${gmailAccount}? This will affect all users.`)) {
      return;
    }

    try {
      setRemovingAccount(gmailAccount);
      await removeGBPAccount(gmailAccount);
      setAccounts(accounts.filter(acc => acc.gmailAccount !== gmailAccount));
    } catch (error) {
      console.error('Failed to remove account:', error);
      if (error.message.includes('403')) {
        alert('Only administrators can remove accounts.');
      } else {
        alert('Failed to remove account. Please try again.');
      }
    } finally {
      setRemovingAccount(null);
    }
  };

  const formatExpiryDate = (expiresAt) => {
    const date = new Date(expiresAt);
    const now = new Date();
    const isExpired = date < now;
    const diffHours = Math.abs(date - now) / (1000 * 60 * 60);

    if (isExpired) {
      return { text: 'Expired', color: 'text-red-600', isExpired: true };
    } else if (diffHours < 24) {
      return { text: `Expires in ${Math.round(diffHours)} hours`, color: 'text-orange-600', isExpired: false };
    } else {
      return { text: `Expires ${date.toLocaleDateString()}`, color: 'text-gray-600', isExpired: false };
    }
  };

  if (loading) {
    return (
      <div className="p-6 text-center">
        <RefreshCw className="w-8 h-8 mx-auto mb-2 animate-spin text-purple-600" />
        <p className="text-gray-600">Loading accounts...</p>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <h2 className="text-2xl font-bold text-gray-900">Google Business Profile Accounts</h2>
            <span className="px-2 py-1 text-xs font-semibold text-purple-700 bg-purple-100 rounded-full flex items-center gap-1">
              <Users className="w-3 h-3" />
              Shared
            </span>
          </div>
          <p className="text-gray-600 mt-1">
            {isAdmin
              ? 'Add Gmail accounts to provide Google Business Profile access to all users via MCP tools'
              : 'View available Google Business Profile accounts (use them via chat with MCP tools)'}
          </p>
        </div>
        {isAdmin && (
          <button
            onClick={handleAddAccount}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Account
          </button>
        )}
      </div>

      {!isAdmin && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-2">
          <Shield className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800">
            <strong>Note:</strong> Only administrators can add or remove accounts. Contact an admin to add more Google Business Profile accounts.
          </div>
        </div>
      )}

      {accounts.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <ExternalLink className="w-12 h-12 mx-auto mb-3 text-gray-400" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No accounts connected</h3>
          <p className="text-gray-600 mb-4">
            {isAdmin
              ? 'Add your first Gmail account to enable Google Business Profile tools for all users'
              : 'Ask an administrator to connect a Gmail account to enable Google Business Profile features'}
          </p>
          {isAdmin && (
            <button
              onClick={handleAddAccount}
              className="inline-flex items-center gap-2 px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Connect First Account
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {accounts.map((account) => {
            const expiry = formatExpiryDate(account.expiresAt);

            return (
              <div
                key={account.id}
                className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-lg hover:border-purple-300 transition-colors"
              >
                <div className="flex items-center gap-4">
                  <div className={`p-2 rounded-full ${expiry.isExpired ? 'bg-red-100' : 'bg-green-100'}`}>
                    {expiry.isExpired ? (
                      <XCircle className="w-5 h-5 text-red-600" />
                    ) : (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    )}
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{account.gmailAccount}</h3>
                    <div className="flex items-center gap-3 mt-1">
                      <p className={`text-sm ${expiry.color}`}>
                        {expiry.text}
                      </p>
                      <span className="text-xs text-gray-500">
                        Added by {account.addedBy}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleTestConnection(account.gmailAccount)}
                    disabled={testingAccount === account.gmailAccount}
                    className="flex items-center gap-2 px-3 py-2 text-sm text-purple-600 hover:bg-purple-50 rounded-lg transition-colors disabled:opacity-50"
                  >
                    {testingAccount === account.gmailAccount ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <RefreshCw className="w-4 h-4" />
                    )}
                    Test
                  </button>

                  {expiry.isExpired && isAdmin && (
                    <button
                      onClick={handleAddAccount}
                      className="flex items-center gap-2 px-3 py-2 text-sm text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
                      title="Re-authenticate this account"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Re-auth
                    </button>
                  )}

                  {isAdmin && (
                    <button
                      onClick={() => handleRemoveAccount(account.gmailAccount)}
                      disabled={removingAccount === account.gmailAccount}
                      className="flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                    >
                      {removingAccount === account.gmailAccount ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                      Remove
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <h3 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
          <Users className="w-5 h-5" />
          Available for All Users via MCP Tools
        </h3>
        <ul className="space-y-1 text-sm text-blue-800">
          <li>• Query business locations across all connected accounts</li>
          <li>• Access phone call insights and tracking data</li>
          <li>• View and respond to customer reviews</li>
          <li>• Track performance metrics and analytics</li>
          <li>• Manage business posts and updates</li>
          <li>• All accessible through natural language chat interface</li>
        </ul>
      </div>

      {accounts.length > 0 && (
        <div className="mt-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
          <h3 className="font-semibold text-gray-900 mb-2">Using in Chat</h3>
          <p className="text-sm text-gray-700 mb-2">
            Example prompts to use these accounts:
          </p>
          <ul className="space-y-1 text-sm text-gray-600 font-mono bg-white p-3 rounded border border-gray-200">
            <li>• "List all locations for {accounts[0]?.gmailAccount}"</li>
            <li>• "Show me recent reviews"</li>
            <li>• "Get call insights for this month"</li>
            <li>• "What are the performance metrics?"</li>
          </ul>
        </div>
      )}
    </div>
  );
};

export default GoogleBusinessSettings;
